import type { QaEnvironmentConfig } from "@yagni/shared";
import { Button, Input } from "../ui/index.js";
import { Row, RowLink } from "./environmentControls.js";

interface Props {
  config: QaEnvironmentConfig;
  credentialNames: string[];
  onChange(patch: Partial<QaEnvironmentConfig>): void;
  /** Opens another settings row: the ones these warnings name. */
  onOpenRow?: (section: string) => void;
}

export function EnvironmentPreviewAccess({
  config,
  credentialNames,
  onChange,
  onOpenRow,
}: Props) {
  const entries = config.previewAccess ?? [];
  const approved = config.allowedOrigins.filter((origin) => {
    try {
      const url = new URL(origin);
      return (
        url.protocol === "https:" &&
        url.origin === origin &&
        !/(^|\.)google(?:usercontent)?\.com$/.test(url.hostname) &&
        !(config.authOrigins ?? []).some(
          (auth) => new URL(auth).hostname === url.hostname,
        )
      );
    } catch {
      return false;
    }
  });
  const update = (
    index: number,
    headers: NonNullable<
      QaEnvironmentConfig["previewAccess"]
    >[number]["headers"],
  ) =>
    onChange({
      previewAccess: entries.map((entry, i) =>
        i === index ? { ...entry, headers } : entry,
      ),
    });
  return (
    <>
      <Row
        title="Add protected origin"
        help="Saved credentials for an approved preview or test API. Fletcher sends these headers only to that origin and keeps them out of application commands."
      >
        <span className="wrk-srow-controls qa-service">
          <label>
            Add protected origin
            <select
              value=""
              disabled={entries.length >= 5}
              onChange={(event) => {
                const origin = event.target.value;
                if (
                  entries.length < 5 &&
                  approved.includes(origin) &&
                  !entries.some((entry) => entry.origin === origin)
                )
                  onChange({
                    previewAccess: [
                      ...entries,
                      { origin, headers: [{ name: "", secretName: "" }] },
                    ],
                  });
              }}
            >
              <option value="">Choose an approved HTTPS origin</option>
              {approved
                .filter(
                  (origin) => !entries.some((entry) => entry.origin === origin),
                )
                .map((origin) => (
                  <option key={origin}>{origin}</option>
                ))}
            </select>
          </label>
        </span>
      </Row>
      {entries.map((entry, index) => (
        <Row key={entry.origin} title={entry.origin}>
          <span className="wrk-srow-controls qa-service">
            {!approved.includes(entry.origin) && (
              <p role="alert">
                This origin is no longer approved for preview credentials.
                Approve it under{" "}
                <RowLink section="network" onOpenRow={onOpenRow}>
                  Network
                </RowLink>{" "}
                or remove it.
              </p>
            )}
            {entry.headers.map((header, headerIndex) => (
              <div className="qa-service" key={headerIndex}>
                <label>
                  Header name {headerIndex + 1} for {entry.origin}
                  <Input
                    value={header.name}
                    placeholder="x-preview-access"
                    onChange={(event) =>
                      update(
                        index,
                        entry.headers.map((item, i) =>
                          i === headerIndex
                            ? { ...item, name: event.target.value }
                            : item,
                        ),
                      )
                    }
                  />
                </label>
                <label>
                  Credential {headerIndex + 1} for {entry.origin}
                  <select
                    value={header.secretName}
                    onChange={(event) =>
                      update(
                        index,
                        entry.headers.map((item, i) =>
                          i === headerIndex
                            ? { ...item, secretName: event.target.value }
                            : item,
                        ),
                      )
                    }
                  >
                    <option value="">Choose a saved credential</option>
                    {header.secretName &&
                      !credentialNames.includes(header.secretName) && (
                        <option value={header.secretName}>
                          {header.secretName} (missing)
                        </option>
                      )}
                    {credentialNames.map((name) => (
                      <option key={name}>{name}</option>
                    ))}
                  </select>
                </label>
                {header.secretName &&
                  !credentialNames.includes(header.secretName) && (
                    <p role="alert">
                      {header.secretName} is not saved. Add it under{" "}
                      <RowLink section="credentials" onOpenRow={onOpenRow}>
                        Variables
                      </RowLink>{" "}
                      before testing.
                    </p>
                  )}
                {entry.headers.length > 1 && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() =>
                      update(
                        index,
                        entry.headers.filter((_, i) => i !== headerIndex),
                      )
                    }
                  >
                    Remove header {headerIndex + 1} for {entry.origin}
                  </Button>
                )}
              </div>
            ))}
            <div className="qa-row-actions">
              <Button
                type="button"
                variant="secondary"
                disabled={entry.headers.length >= 5}
                onClick={() =>
                  update(index, [
                    ...entry.headers,
                    { name: "", secretName: "" },
                  ])
                }
              >
                Add header for {entry.origin}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() =>
                  onChange({
                    previewAccess: entries.filter((_, i) => i !== index),
                  })
                }
              >
                Remove protected origin {entry.origin}
              </Button>
            </div>
          </span>
        </Row>
      ))}
      {entries.length > 0 && (
        <Row title="Authorization">
          <span className="wrk-srow-controls qa-note">
            Authorization replaces the application&rsquo;s own Authorization
            header at that origin. Protected WebSocket connections are not
            supported.
          </span>
        </Row>
      )}
    </>
  );
}
